<template>
  <div v-if="isVisible">
    <div v-if="showTitle" class="field__inner-title mb-3">
      <div>{{ schema.title }}</div>
      <p v-if="schema.help">{{ schema.help || '' }}</p>
    </div>

    <div class="row">
      <div class="col-md-6">
        <v-menu
          ref="datePicker"
          :close-on-content-click="false"
          v-model="datePickerModal"
          :nudge-right="40"
          transition="scale-transition"
          min-width="290px"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <div v-on="!isDisabled && on" style="position: relative">
              <v-text-field
                slot="activator"
                v-model="localDate"
                @input="handleDateTimeChange('date')"
                readonly
                :label="fieldLabel"
                :class="fieldClasses"
                :rules="rules"
                hint=""
                persistent-hint
                :prepend-inner-icon="getInputIcon('today')"
                :disabled="isDisabled"
                clearable
              />
            </div>
          </template>
          <v-date-picker
            v-model="localDate"
            @input="handleDateTimeChange('date')"
            color="primary"
            no-title
            :min="schema.min"
            :max="schema.max"
          />
        </v-menu>
      </div>

      <div class="col-md-6">
        <v-menu
          ref="timePicker"
          :close-on-content-click="false"
          v-model="timePickerModal"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <div v-on="!isDisabled && on">
              <v-text-field
                slot="activator"
                :class="fieldClasses"
                v-model="startTime"
                @input="handleDateTimeChange('time')"
                label="Time"
                hint=""
                persistent-hint
                readonly
                :prepend-inner-icon="getInputIcon('access_time')"
                :disabled="isDisabled"
                clearable
              />
            </div>
          </template>
          <v-time-picker
            v-if="timePickerModal"
            v-model="localTime"
            @input="handleDateTimeChange('time')"
            color="appMain"
            :allowed-minutes="allowedMinutes"
            ampm-in-title
            @click:minute="timePickerModal = false"
          />
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
import GenericField from './GenericField';
import {
  format,
  formatISO,
  parse,
  roundToNearestMinutes,
  isValid,
} from 'date-fns';
import { convertTimeToAmPm } from '@/filters/helpers';

export default {
  name: 'DateTimeField',

  mixins: [GenericField],

  data() {
    return {
      datePickerModal: false,
      timePickerModal: false,
      localDate: '',
      localTime: '',
    };
  },

  computed: {
    startTime: {
      get() {
        return convertTimeToAmPm(this.localTime);
      },
      set(value) {
        this.localTime = value;
      },
    },

    fieldLabel() {
      const label = this.schema?.label;

      return label && !this.showTitle ? label : 'Date';
    },
  },

  watch: {
    value: 'syncDateTime',
  },

  methods: {
    allowedMinutes(v) {
      return v % 15 === 0;
    },

    syncDateTime(value) {
      if (!value) {
        this.localDate = '';
        this.localTime = '';
        return;
      }

      const date = this.parseIncomingValue(value);

      if (isValid(date)) {
        this.setLocalDateTime(date);
      }
    },

    parseIncomingValue(value) {
      if (typeof value === 'number') {
        return new Date(value * 1000);
      }

      return new Date(value);
    },

    setLocalDateTime(date) {
      this.localDate = format(date, 'yyyy-MM-dd');
      this.localTime = format(date, 'HH:mm');
    },

    resetDateTime() {
      this.localDate = '';
      this.localTime = '';
      this.localValue = '';
    },

    getInputIcon(icon) {
      return this.schema?.hideLabel ? icon : '';
    },

    parseLocalDateTime() {
      return parse(
        `${this.localDate} ${this.localTime}`,
        'yyyy-MM-dd HH:mm',
        new Date()
      );
    },

    updateLocalValue() {
      if (!this.localDate) {
        this.resetDateTime();
        return;
      }

      if (!this.localTime) {
        this.localTime = '00:00';
      }

      const date = this.parseLocalDateTime();

      if (!isValid(date)) return;

      this.localValue = formatISO(date);
    },

    handleDateTimeChange(type) {
      this.updateLocalValue();

      if (type === 'date') {
        this.datePickerModal = false;
      }
    },

    initializeDateTime() {
      if (!this.localValue && this.schema.noDefault) return;

      let date = roundToNearestMinutes(new Date(), {
        nearestTo: 15,
      });

      if (this.localValue) {
        date = this.parseIncomingValue(
          this.isStringDateMode() ? this.localValue : Number(this.localValue)
        );
      }

      if (!isValid(date)) return;

      this.setLocalDateTime(date);
      this.updateLocalValue();
    },

    isStringDateMode() {
      return typeof this.localValue === 'string' && !this.schema.timestamp;
    },
  },

  mounted() {
    this.initializeDateTime();
  },
};
</script>
